import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import * as globals from '../../components/GlobalScripts.js';
import '../../styles/cliente/menu_pedidos_wizard.css';

import {
    DAY_LABELS,
    ORDER_CUTOFF_HOUR_EC,
    computeDayDateMapFromWeekRange,
    createOrder,
    dateOnly,
    fetchOrdersWeek,
    fetchPlatillosMap,
    fetchPublishedMenu,
    getMinOrderDayEc,
    getTodayEc,
    updateOrder,
} from './clienteMenuApi.js';

function BackIcon({ size = 18 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}
function CheckIcon({ size = 18 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

function WarningIcon({ size = 18 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
            <path
                d="M12 2 1 21h22L12 2Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinejoin="round"
            />
            <path d="M12 9v5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            <path d="M12 17h.01" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
        </svg>
    );
}

function ChevronIcon({ dir = 'down', size = 18 }) {
    const rotate = dir === 'up' ? 'rotate(180deg)' : 'rotate(0deg)';
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ transform: rotate }}>
            <path
                d="M6 9l6 6 6-6"
                stroke="currentColor"
                strokeWidth="2.25"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

function ArrowIcon({ dir = 'right', size = 18 }) {
    const rotate = dir === 'left' ? 'rotate(180deg)' : 'rotate(0deg)';
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ transform: rotate }}>
            <path
                d="M10 7l5 5-5 5"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

function fmtNutValue(val, { decimals = 0 } = {}) {
    if (val === null || val === undefined || val === '') return '—';
    const n = Number(val);
    if (Number.isNaN(n)) return String(val);
    return decimals > 0 ? n.toFixed(decimals) : String(Math.round(n));
}

function buildPortionLabel(nut) {
    const p = nut?.porcion || {};
    const t = p?.tamano;
    const u = p?.unidad;
    if (t === null || t === undefined || t === '') return '';
    if (u) return `${t}${u}`;
    return String(t);
}

function MacroStrip({ resumen }) {
    const r = resumen || {};

    return (
        <div className="tw-orderwiz-dish__macroStrip">
            <div className="tw-orderwiz-dish__macroItem">
                <div className="tw-orderwiz-dish__macroVal">{fmtNutValue(r.kcal)}</div>
                <div className="tw-orderwiz-dish__macroLabel">Kilocalorías</div>
            </div>

            <div className="tw-orderwiz-dish__macroItem">
                <div className="tw-orderwiz-dish__macroVal">{fmtNutValue(r.grasas)}g</div>
                <div className="tw-orderwiz-dish__macroLabel">Grasas</div>
            </div>

            <div className="tw-orderwiz-dish__macroItem">
                <div className="tw-orderwiz-dish__macroVal">{fmtNutValue(r.carbohidratos)}g</div>
                <div className="tw-orderwiz-dish__macroLabel">Carbohidratos</div>
            </div>

            <div className="tw-orderwiz-dish__macroItem">
                <div className="tw-orderwiz-dish__macroVal">{fmtNutValue(r.proteinas)}g</div>
                <div className="tw-orderwiz-dish__macroLabel">Proteínas</div>
            </div>
        </div>
    );
}

function InlineNutritionTable({ nutricion }) {
    const nut = nutricion || {};
    const tabla = nut.tabla || {};
    const porcionLabel = buildPortionLabel(nut);
    const porcionesPorEnvase = nut?.porcion?.porciones_por_envase;

    const rowsMain = [
        { label: 'Calorías', value: fmtNutValue(tabla.calorias_kcal), unit: 'kcal' },
        { label: 'Proteínas', value: fmtNutValue(tabla.proteinas_g, { decimals: 1 }), unit: 'g' },
        { label: 'Carbohidratos', value: fmtNutValue(tabla.carbohidratos_totales_g, { decimals: 1 }), unit: 'g' },
        { label: 'Grasas', value: fmtNutValue(tabla.grasas_totales_g, { decimals: 1 }), unit: 'g' },
    ];

    const rowsOther = [
        { label: 'Grasas saturadas', value: fmtNutValue(tabla.grasas_saturadas_g, { decimals: 1 }), unit: 'g' },
        { label: 'Grasas trans', value: fmtNutValue(tabla.grasas_trans_g, { decimals: 1 }), unit: 'g' },
        { label: 'Colesterol', value: fmtNutValue(tabla.colesterol_mg, { decimals: 0 }), unit: 'mg' },
        { label: 'Sodio', value: fmtNutValue(tabla.sodio_mg, { decimals: 0 }), unit: 'mg' },
        { label: 'Carbohidratos totales', value: fmtNutValue(tabla.carbohidratos_totales_g, { decimals: 1 }), unit: 'g' },
        { label: 'Fibra dietética', value: fmtNutValue(tabla.fibra_dietetica_g, { decimals: 1 }), unit: 'g' },
        { label: 'Azúcares totales', value: fmtNutValue(tabla.azucares_totales_g, { decimals: 1 }), unit: 'g' },
        { label: 'Azúcares añadidos', value: fmtNutValue(tabla.azucares_anadidos_g, { decimals: 1 }), unit: 'g' },
    ].filter((r) => r.value !== '—');

    const extra = Array.isArray(nut.extra) ? nut.extra : [];
    const notas = String(nut.notas || '').trim();

    const hasSomething =
        rowsMain.some((r) => r.value !== '—') ||
        rowsOther.length > 0 ||
        extra.length > 0 ||
        Boolean(notas) ||
        Boolean(porcionLabel) ||
        (porcionesPorEnvase !== null && porcionesPorEnvase !== undefined && porcionesPorEnvase !== '');

    if (!hasSomething) {
        return <div className="tw-orderwiz-dish__nutriEmpty">Sin tabla nutrimental disponible.</div>;
    }

    return (
        <div className="tw-orderwiz-dish__nutriPanelInner">
            {(porcionLabel || porcionesPorEnvase !== null && porcionesPorEnvase !== undefined && porcionesPorEnvase !== '') ? (
                <div className="tw-orderwiz-dish__nutriMeta">
                    {porcionLabel ? <>Porción: {porcionLabel}</> : null}
                    {porcionLabel && (porcionesPorEnvase !== null && porcionesPorEnvase !== undefined && porcionesPorEnvase !== '') ? <> · </> : null}
                    {(porcionesPorEnvase !== null && porcionesPorEnvase !== undefined && porcionesPorEnvase !== '') ? (
                        <>Porciones por envase: {String(porcionesPorEnvase)}</>
                    ) : null}
                </div>
            ) : null}

            <table className="tw-orderwiz-dish__nutriTable">
                <tbody>
                    {rowsMain.map((r) => (
                        <tr key={r.label}>
                            <td>{r.label}</td>
                            <td className="tw-orderwiz-dish__nutriVal">
                                {r.value} {r.unit}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {rowsOther.length ? (
                <table className="tw-orderwiz-dish__nutriTable tw-orderwiz-dish__nutriTable--secondary">
                    <tbody>
                        {rowsOther.map((r) => (
                            <tr key={r.label}>
                                <td>{r.label}</td>
                                <td className="tw-orderwiz-dish__nutriVal">
                                    {r.value} {r.unit}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            ) : null}

            {extra.length ? (
                <div className="tw-orderwiz-dish__nutriExtra">
                    <div className="tw-orderwiz-dish__nutriExtraTitle">Otros nutrientes</div>
                    <table className="tw-orderwiz-dish__nutriTable">
                        <thead>
                            <tr>
                                <th>Nutriente</th>
                                <th className="tw-orderwiz-dish__nutriVal">Cantidad</th>
                                <th className="tw-orderwiz-dish__nutriVal">%VD</th>
                            </tr>
                        </thead>
                        <tbody>
                            {extra.map((row, idx) => {
                                const nombre = row?.nombre ?? '—';
                                const cantidad = row?.cantidad ?? '—';
                                const unidad = row?.unidad ? String(row.unidad) : '';
                                const vd = row?.vd ?? '—';

                                return (
                                    <tr key={`${nombre}-${idx}`}>
                                        <td>{nombre}</td>
                                        <td className="tw-orderwiz-dish__nutriVal">
                                            {cantidad}{unidad ? ` ${unidad}` : ''}
                                        </td>
                                        <td className="tw-orderwiz-dish__nutriVal">{vd}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            ) : null}

            {notas ? <div className="tw-orderwiz-dish__nutriNotes">{notas}</div> : null}
        </div>
    );
}

function DishCard({ dish, subtitle, selected, onSelect }) {
    const [imgIndex, setImgIndex] = useState(0);
    const [nutriOpen, setNutriOpen] = useState(false);
    const [descOpen, setDescOpen] = useState(false);
    const descRef = useRef(null);
    const [canExpandDesc, setCanExpandDesc] = useState(false);

    const images = useMemo(() => {
        const arr = Array.isArray(dish?.imagenes) ? dish.imagenes : [];
        return arr.map((x) => String(x || '').trim()).filter(Boolean);
    }, [dish?.id, dish?.imagenes]);

    useEffect(() => {
        setImgIndex(0);
        setNutriOpen(false);
        setDescOpen(false);
        setCanExpandDesc(false);
    }, [dish?.id]);

    useEffect(() => {
        if (descOpen) return;
        const el = descRef.current;
        if (!el) return;
        setCanExpandDesc(el.scrollHeight > el.clientHeight + 2);
    }, [dish?.id, dish?.descripcion, descOpen]);

    const activeImg = images.length ? images[Math.min(imgIndex, images.length - 1)] : '';
    const resumen = dish?.nutricion?.resumen || dish?.macros || {};
    const portionLabel = buildPortionLabel(dish?.nutricion);

    const goPrev = (e) => {
        e?.stopPropagation?.();
        if (images.length <= 1) return;
        setImgIndex((p) => (p - 1 + images.length) % images.length);
    };

    const goNext = (e) => {
        e?.stopPropagation?.();
        if (images.length <= 1) return;
        setImgIndex((p) => (p + 1) % images.length);
    };

    const toggleNutri = (e) => {
        e?.stopPropagation?.();
        setNutriOpen((p) => !p);
    };

    return (
        <div
            className={`tw-orderwiz-dish ${selected ? 'tw-orderwiz-dish--selected' : ''}`}
            role="button"
            tabIndex={0}
            onClick={onSelect}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') onSelect?.();
            }}
        >
            <div className="tw-orderwiz-dish__img" aria-label={dish?.nombre || 'Platillo'}>
                {activeImg ? (
                    <img src={activeImg} alt={dish?.nombre || 'Platillo'} loading="lazy" />
                ) : (
                    <div className="tw-orderwiz-dish__imgph">Sin imagen</div>
                )}

                {subtitle ? <div className="tw-orderwiz-dish__scope">{subtitle}</div> : null}

                {images.length > 1 ? (
                    <>
                        <button
                            type="button"
                            className="tw-orderwiz-dish__imgNav tw-orderwiz-dish__imgNav--prev"
                            onClick={goPrev}
                            aria-label="Imagen anterior"
                        >
                            <ArrowIcon dir="left" />
                        </button>

                        <button
                            type="button"
                            className="tw-orderwiz-dish__imgNav tw-orderwiz-dish__imgNav--next"
                            onClick={goNext}
                            aria-label="Siguiente imagen"
                        >
                            <ArrowIcon dir="right" />
                        </button>

                        <div className="tw-orderwiz-dish__imgDots" aria-hidden="true">
                            {images.map((_, i) => (
                                <span
                                    key={i}
                                    className={`tw-orderwiz-dish__imgDot ${i === imgIndex ? 'is-active' : ''}`}
                                />
                            ))}
                        </div>
                    </>
                ) : null}
            </div>

            <div className="tw-orderwiz-dish__info">
                <div className="tw-orderwiz-dish__head">
                    <div className="tw-orderwiz-dish__name">{dish?.nombre}</div>
                    {portionLabel ? <div className="tw-orderwiz-dish__portion">({portionLabel})</div> : null}
                </div>

                {dish?.descripcion ? (
                    <div className="tw-orderwiz-dish__descwrap">
                        <div
                            ref={descRef}
                            className={`tw-orderwiz-dish__desc ${descOpen ? 'is-expanded' : ''}`}
                        >
                            {dish.descripcion}
                        </div>
                        {canExpandDesc ? (
                            <button
                                type="button"
                                className="tw-orderwiz-dish__more"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setDescOpen((v) => !v);
                                }}
                            >
                                {descOpen ? 'Ver menos' : 'Ver descripción completa'}
                                <ChevronIcon dir={descOpen ? 'up' : 'down'} size={16} />
                            </button>
                        ) : null}
                    </div>
                ) : null}
                <MacroStrip resumen={resumen} />

                <button
                    type="button"
                    className="tw-orderwiz-dish__nutriToggle"
                    onClick={toggleNutri}
                    aria-expanded={nutriOpen ? 'true' : 'false'}
                >
                    <span>Ver tabla nutrimental</span>
                    <ChevronIcon dir={nutriOpen ? 'up' : 'down'} />
                </button>

                {nutriOpen ? (
                    <div className="tw-orderwiz-dish__nutriPanel" onClick={(e) => e.stopPropagation()}>
                        <InlineNutritionTable nutricion={dish?.nutricion} />
                    </div>
                ) : null}

                {dish?.alergenos ? (
                    <div className="tw-orderwiz-dish__alergenos" onClick={(e) => e.stopPropagation()}>
                        <WarningIcon size={18} />
                        <span>{dish.alergenos}</span>
                    </div>
                ) : null}

                <button
                    type="button"
                    className={`tw-orderwiz-dish__selectBtn ${selected ? 'is-selected' : ''}`}
                    onClick={(e) => {
                        e.stopPropagation();
                        onSelect?.();
                    }}
                >
                    {selected ? 'Seleccionado' : 'Añadir'}
                </button>
            </div>
        </div>
    );
}

function parseDishInfo(order) {
    const raw = order?.dish_info;
    if (!raw) return null;
    if (typeof raw === 'object') return raw;
    try {
        return JSON.parse(raw);
    } catch {
        return null;
    }
}

export default function Cliente_menu_pedidos_wizard() {
    const navigate = useNavigate();
    const { dayKey: rawDayKey, type: rawType } = useParams();
    const location = useLocation();
    const topRef = useRef(null);

    const dayKey = String(rawDayKey || '').toLowerCase();
    const slotType = String(rawType || '').toLowerCase();

    const hoy = getTodayEc();
    const minOrderDayIso = getMinOrderDayEc();

    const state = location.state || {};
    const initialMenu = state.menu || null;
    const initialDishMap = state.dishMap || null;
    const initialAccount = state.account || null;
    const initialExistingOrder = state.existingOrder || null;

    const [loading, setLoading] = useState(true);
    const [menu, setMenu] = useState(initialMenu);
    const [dishMap, setDishMap] = useState(initialDishMap || {});
    const [account, setAccount] = useState(initialAccount || (globals.get_user() || {}));

    const envios = Number(account?.envios || 0) || 0;
    const nivel = Number(account?.nivel || 1) || 1;
    const expDay = String(account?.envios_expira_at || '').slice(0, 10);
    const isExpired = !!expDay && expDay < hoy;

    const [existingOrder, setExistingOrder] = useState(initialExistingOrder);

    const [resolvedDayIso, setResolvedDayIso] = useState(String(state.dayIso || '').slice(0, 10));

    const busyRef = useRef(0);
    const busyWrap = async (fn) => {
        try {
            if (busyRef.current === 0) {
                globals.openViewCharge();
                await new Promise(requestAnimationFrame);
            }
            busyRef.current += 1;
            return await fn();
        } finally {
            busyRef.current = Math.max(0, busyRef.current - 1);
            if (busyRef.current === 0) globals.closeViewCharge();
        }
    };

    const isValidType = slotType === 'almuerzo' || slotType === 'cena';
    const isValidDayKey = !!DAY_LABELS?.[dayKey];

    const dayDateMap = useMemo(() => {
        const s = menu?.week?.start_date;
        const e = menu?.week?.end_date;
        return computeDayDateMapFromWeekRange(s, e);
    }, [menu]);

    useEffect(() => {
        if (resolvedDayIso) return;
        const iso = String(dayDateMap?.[dayKey] || '').slice(0, 10);
        if (iso) setResolvedDayIso(iso);
    }, [dayDateMap, dayKey]);

    const isFutureDay = !!resolvedDayIso && resolvedDayIso > hoy;
    const isCreateAllowedDay = !!resolvedDayIso && resolvedDayIso >= minOrderDayIso;
    const isCreateBlockedByCutoff = !!resolvedDayIso && resolvedDayIso > hoy && resolvedDayIso < minOrderDayIso;
    const isUpdateAllowedDay = !!resolvedDayIso && resolvedDayIso >= minOrderDayIso;
    const isUpdateBlockedByCutoff = !!resolvedDayIso && resolvedDayIso > hoy && resolvedDayIso < minOrderDayIso;
    const createBlockedMsg = isCreateBlockedByCutoff
        ? `Los pedidos para ${resolvedDayIso} cerraron a las ${ORDER_CUTOFF_HOUR_EC}:00 hora Ecuador.`
        : 'No puedes pedir para hoy o días pasados.';
    const updateBlockedMsg = isUpdateBlockedByCutoff
        ? `Los cambios para ${resolvedDayIso} cerraron a las ${ORDER_CUTOFF_HOUR_EC}:00 hora Ecuador.`
        : 'Solo puedes actualizar pedidos futuros.';

    const optionsForSlot = useMemo(() => {
        const opts = Array.isArray(menu?.days?.[dayKey]?.options) ? menu.days[dayKey].options : [];
        return opts.filter((o) => {
            if (slotType === 'almuerzo') return o.scope === 'lunch' || o.scope === 'both';
            if (slotType === 'cena') return o.scope === 'dinner' || o.scope === 'both';
            return false;
        });
    }, [menu, dayKey, slotType]);

    const prefill = useMemo(() => {
        const parsed = parseDishInfo(existingOrder);
        const selected = parsed?.selected || parsed || {};
        return {
            option_id: selected?.option_id ?? selected?.optionId ?? null,
            main_id: selected?.main_id ?? selected?.mainId ?? null,
            entry_id: selected?.entry_id ?? selected?.entryId ?? null,
            snack_id: selected?.snack_id ?? selected?.snackId ?? null,
        };
    }, [existingOrder]);

    const [step, setStep] = useState(0);
    const [pickedOptionId, setPickedOptionId] = useState(prefill.option_id || null);
    const [pickedEntryId, setPickedEntryId] = useState(prefill.entry_id ?? null);
    const [pickedSnackId, setPickedSnackId] = useState(prefill.snack_id ?? null);

    const touchedRef = useRef({ option: false, entry: false, snack: false });
    const prefillSyncRef = useRef(false);

    const pickedOption = useMemo(() => {
        return optionsForSlot.find((o) => String(o.id) === String(pickedOptionId)) || null;
    }, [optionsForSlot, pickedOptionId]);

    const mainDish = useMemo(() => {
        const mainId = pickedOption?.main_id || prefill.main_id;
        return mainId ? dishMap?.[String(mainId)] : null;
    }, [pickedOption, prefill.main_id, dishMap]);

    const entryChoices = useMemo(() => {
        const ids = Array.isArray(pickedOption?.entry_ids) ? pickedOption.entry_ids : [];
        return ids.map((id) => dishMap?.[String(id)]).filter(Boolean);
    }, [pickedOption, dishMap]);

    const snackChoices = useMemo(() => {
        if (slotType === 'cena') return [];
        const ids = Array.isArray(pickedOption?.snack_ids) ? pickedOption.snack_ids : [];
        return ids.map((id) => dishMap?.[String(id)]).filter(Boolean);
    }, [pickedOption, dishMap]);

    const canShowEntryStep = entryChoices.length > 0;
    const canShowSnackStep = slotType !== 'cena' && snackChoices.length > 0 && nivel >= 2;

    const steps = useMemo(() => {
        const out = [{ key: 'main', label: 'Plato fuerte' }];
        if (canShowEntryStep) out.push({ key: 'entry', label: 'Entrada' });
        if (canShowSnackStep) out.push({ key: 'snack', label: 'Snack' });
        out.push({ key: 'sum', label: 'Resumen' });
        return out;
    }, [canShowEntryStep, canShowSnackStep]);

    const maxStep = steps.length - 1;
    const stepKey = steps[step]?.key;

    const mode = useMemo(() => {
        if (existingOrder && existingOrder.status !== 'cancelado' && isFutureDay) return 'update';
        return 'create';
    }, [existingOrder, isFutureDay]);

    useEffect(() => {
        if (!existingOrder) return;

        const nextOptionId = prefill.option_id || null;
        const nextEntryId = prefill.entry_id ?? null;
        const nextSnackId = prefill.snack_id ?? null;

        prefillSyncRef.current = true;
        try {
            if (!touchedRef.current.option && nextOptionId && String(nextOptionId) !== String(pickedOptionId || '')) {
                setPickedOptionId(nextOptionId);
            }
            if (!touchedRef.current.entry) setPickedEntryId(nextEntryId);
            if (!touchedRef.current.snack) setPickedSnackId(nextSnackId);
        } finally {
            setTimeout(() => {
                prefillSyncRef.current = false;
            }, 0);
        }
    }, [existingOrder]);

    useEffect(() => {
        if (prefillSyncRef.current) return;
        if (!touchedRef.current.option) return;
        setPickedEntryId(null);
        setPickedSnackId(null);
    }, [pickedOptionId]);

    const loadAll = async () => {
        setLoading(true);

        try {
            await busyWrap(async () => {
                let m = menu;
                if (!m) m = await fetchPublishedMenu();
                setMenu(m);

                const mapDates = computeDayDateMapFromWeekRange(m?.week?.start_date, m?.week?.end_date);
                const iso = String(state.dayIso || mapDates?.[dayKey] || '').slice(0, 10);
                if (iso) setResolvedDayIso(iso);

                let map = dishMap;
                if (!map || Object.keys(map).length === 0) map = await fetchPlatillosMap();
                setDishMap(map);

                const weekStart = m?.week?.start_date;
                const weekEnd = m?.week?.end_date;

                const { orders: ords, account: acc } = await fetchOrdersWeek({ start_date: weekStart, end_date: weekEnd });
                const ordList = Array.isArray(ords) ? ords : [];

                if (acc) {
                    setAccount(acc);
                    const current = globals.get_user() || {};
                    localStorage.setItem('user', JSON.stringify({ ...current, ...acc }));
                }

                if (!existingOrder && iso && isValidType) {
                    const candidate =
                        ordList
                            .filter((o) => dateOnly(o.order_date) === iso && o.type === slotType && o.status !== 'cancelado')
                            .sort((a, b) => (Number(b.id) || 0) - (Number(a.id) || 0))[0] || null;

                    setExistingOrder(candidate);
                }

                setLoading(false);
            });
        } catch (e) {
            setLoading(false);
            globals.createAlert?.({
                type: 'error',
                msg_title: 'Error',
                msg: String(e?.message || 'No se pudo cargar'),
                effect: 'opacidad 2s, desplazamiento 2s',
                time_life: 4000,
            });
        }
    };

    useEffect(() => {
        loadAll();
    }, []);

    const goBack = () => navigate('/cliente/menu');

    const goNext = () => setStep((s) => Math.min(maxStep, s + 1));
    const goPrev = () => setStep((s) => Math.max(0, s - 1));

    const canConfirmCreate = isCreateAllowedDay && !isExpired && envios > 0;
    const canConfirmUpdate = isUpdateAllowedDay;

    const buildDishInfoPayload = () => {
        const option = pickedOption;
        const mainId = option?.main_id || prefill.main_id || null;

        const entryId = pickedEntryId || null;
        const snackId = (slotType !== 'cena' && canShowSnackStep) ? pickedSnackId : null;

        const main = mainId ? dishMap?.[String(mainId)] : null;
        const entry = entryId ? dishMap?.[String(entryId)] : null;
        const snack = snackId ? dishMap?.[String(snackId)] : null;

        return {
            selected: {
                option_id: option?.id ?? null,
                main_id: mainId,
                entry_id: entryId,
                snack_id: snackId,
            },
            snapshot: {
                main: main ? { id: main.id, nombre: main.nombre, descripcion: main.descripcion, alergenos: main.alergenos, macros: main.macros, imagenes: main.imagenes } : null,
                entry: entry ? { id: entry.id, nombre: entry.nombre, descripcion: entry.descripcion, alergenos: entry.alergenos, macros: entry.macros, imagenes: entry.imagenes } : null,
                snack: snack ? { id: snack.id, nombre: snack.nombre, descripcion: snack.descripcion, alergenos: snack.alergenos, macros: snack.macros, imagenes: snack.imagenes } : null,
            },
            meta: {
                day_key: dayKey,
                day_iso: resolvedDayIso,
                type: slotType,
                week_start: menu?.week?.start_date || null,
                week_end: menu?.week?.end_date || null,
            },
        };
    };

    const handleConfirm = async () => {
        if (!pickedOption) {
            globals.createAlert?.({
                type: 'advertencia',
                msg_title: 'Falta selección',
                msg: 'Elige un plato fuerte.',
                effect: 'opacidad 2s, desplazamiento 2s',
                time_life: 3000,
            });
            return;
        }

        if (mode === 'create' && !isCreateAllowedDay) {
            globals.createAlert?.({
                type: 'advertencia',
                msg_title: 'No permitido',
                msg: createBlockedMsg,
                effect: 'opacidad 2s, desplazamiento 2s',
                time_life: 3000,
            });
            return;
        }

        if (mode !== 'create' && !isUpdateAllowedDay) {
            globals.createAlert?.({
                type: 'advertencia',
                msg_title: 'No permitido',
                msg: updateBlockedMsg,
                effect: 'opacidad 2s, desplazamiento 2s',
                time_life: 3000,
            });
            return;
        }

        try {
            await busyWrap(async () => {
                const dish_info = buildDishInfoPayload();

                if (mode === 'create') {
                    if (!canConfirmCreate) {
                        globals.createAlert?.({
                            type: 'advertencia',
                            msg_title: 'No disponible',
                            msg: isExpired ? 'Tu plan está expirado.' : envios <= 0 ? 'No tienes envíos.' : 'No disponible.',
                            effect: 'opacidad 2s, desplazamiento 2s',
                            time_life: 3500,
                        });
                        return;
                    }

                    const res = await createOrder({ order_day: resolvedDayIso, type: slotType, dish_info });
                    if (res?.mensaje !== 'success') {
                        globals.createAlert?.({
                            type: 'error',
                            msg_title: 'Error',
                            msg: res?.mensaje || 'No se pudo crear',
                            effect: 'opacidad 2s, desplazamiento 2s',
                            time_life: 4000,
                        });
                        return;
                    }

                    globals.createAlert?.({
                        type: 'success',
                        msg_title: 'Listo',
                        msg: 'Pedido programado.',
                        effect: 'opacidad 2s, desplazamiento 2s',
                        time_life: 2500,
                    });

                    goBack();
                    return;
                }

                if (!existingOrder?.id) {
                    globals.createAlert?.({
                        type: 'error',
                        msg_title: 'Error',
                        msg: 'No se encontró el pedido a actualizar.',
                        effect: 'opacidad 2s, desplazamiento 2s',
                        time_life: 4000,
                    });
                    return;
                }

                if (!canConfirmUpdate) {
                    globals.createAlert?.({
                        type: 'advertencia',
                        msg_title: 'No permitido',
                        msg: updateBlockedMsg,
                        effect: 'opacidad 2s, desplazamiento 2s',
                        time_life: 3500,
                    });
                    return;
                }

                const res = await updateOrder({ id: existingOrder.id, dish_info });
                if (res?.mensaje !== 'success') {
                    globals.createAlert?.({
                        type: 'error',
                        msg_title: 'Error',
                        msg: res?.mensaje || 'No se pudo actualizar',
                        effect: 'opacidad 2s, desplazamiento 2s',
                        time_life: 4000,
                    });
                    return;
                }

                globals.createAlert?.({
                    type: 'success',
                    msg_title: 'Listo',
                    msg: 'Pedido actualizado.',
                    effect: 'opacidad 2s, desplazamiento 2s',
                    time_life: 2500,
                });

                goBack();
            });
        } catch (e) {
            globals.createAlert?.({
                type: 'error',
                msg_title: 'Error',
                msg: String(e?.message || 'No se pudo confirmar'),
                effect: 'opacidad 2s, desplazamiento 2s',
                time_life: 4000,
            });
        }
    };

    useEffect(() => {
        if (slotType === 'cena' && pickedSnackId) setPickedSnackId(null);
    }, [slotType]);

    useEffect(() => {
        if (loading) return;

        requestAnimationFrame(() => {
            topRef.current?.scrollIntoView({
                block: 'start',
                behavior: 'smooth',
            });
        });
    }, [step, loading]);

    const headerTitle = `${DAY_LABELS?.[dayKey] || dayKey || 'Día'} · ${(slotType || '').toUpperCase()}`;

    if (!isValidDayKey || !isValidType) {
        return (
            <div className="tw-orderwiz-page">
                <div className="tw-orderwiz-alert tw-orderwiz-alert--warn">
                    <WarningIcon /> Ruta inválida. Regresa al menú para seleccionar un día y tipo.
                </div>
                <button className="tw-orderwiz-btn tw-orderwiz-btn--primary" onClick={goBack} type="button">
                    <BackIcon /> Volver al menu semanal
                </button>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="tw-orderwiz-page">
                <div className="tw-orderwiz-skel">Cargando...</div>
            </div>
        );
    }

    return (
        <div className="tw-orderwiz-page">
            <div ref={topRef} />
            <div className="tw-orderwiz-head">
                <button className="tw-orderwiz-back" onClick={goBack} type="button">
                    <BackIcon /> Volver al menu semanal
                </button>

                <div className="tw-orderwiz-title">
                    <div className="tw-orderwiz-h1">{headerTitle}</div>
                    <div className="tw-orderwiz-sub">
                        Programar para: <b>{resolvedDayIso || '—'}</b> · {mode === 'create' ? 'Nuevo pedido' : 'Actualizar pedido'}
                    </div>
                </div>

                <div className="tw-orderwiz-badge">
                    <div className="tw-orderwiz-badge__big">{envios}</div>
                    <div className="tw-orderwiz-badge__txt">
                        <div className="tw-orderwiz-badge__label">Envíos</div>
                        <div className="tw-orderwiz-badge__muted">Expiran: {expDay || '—'}</div>
                    </div>
                </div>
            </div>

            {mode === 'create' && !isCreateAllowedDay ? (
                <div className="tw-orderwiz-alert tw-orderwiz-alert--warn">
                    <WarningIcon /> {createBlockedMsg}
                </div>
            ) : null}

            {mode !== 'create' && !isFutureDay ? (
                <div className="tw-orderwiz-alert tw-orderwiz-alert--warn">
                    <WarningIcon /> {updateBlockedMsg}
                </div>
            ) : null}

            {mode !== 'create' && isFutureDay && !isUpdateAllowedDay ? (
                <div className="tw-orderwiz-alert tw-orderwiz-alert--warn">
                    <WarningIcon /> {updateBlockedMsg}
                </div>
            ) : null}

            {mode === 'create' && isExpired ? (
                <div className="tw-orderwiz-alert tw-orderwiz-alert--warn">
                    <WarningIcon /> Tu plan está expirado. No puedes crear nuevos pedidos.
                </div>
            ) : null}

            {mode === 'create' && envios <= 0 ? (
                <div className="tw-orderwiz-alert tw-orderwiz-alert--warn">
                    <WarningIcon /> No tienes envíos disponibles.
                </div>
            ) : null}

            <div className="tw-orderwiz-steps">
                {steps.map((s, idx) => (
                    <div key={s.key} className={`tw-orderwiz-step ${idx === step ? 'tw-orderwiz-step--active' : ''}`}>
                        <div className="tw-orderwiz-step__dot">{idx + 1}</div>
                        <div className="tw-orderwiz-step__label">{s.label}</div>
                    </div>
                ))}
            </div>

            {stepKey === 'main' ? (
                <div className="tw-orderwiz-block">
                    <div className="tw-orderwiz-block__title">Elige tu plato fuerte</div>

                    {optionsForSlot.length === 0 ? (
                        <div className="tw-orderwiz-alert tw-orderwiz-alert--warn">
                            <WarningIcon /> No hay opciones disponibles para este día / {slotType}.
                        </div>
                    ) : null}

                    <div className="tw-orderwiz-grid">
                        {optionsForSlot.map((opt) => {
                            const dish = dishMap?.[String(opt.main_id)] || null;
                            return (
                                <DishCard
                                    key={opt.id}
                                    dish={dish}
                                    subtitle={opt.scope === 'both' ? 'Disponible en almuerzo y cena' : opt.scope === 'lunch' ? 'Almuerzo' : 'Cena'}
                                    selected={String(pickedOptionId) === String(opt.id)}
                                    onSelect={() => {
                                        touchedRef.current.option = true;
                                        setPickedOptionId(opt.id);
                                    }}
                                />
                            );
                        })}
                    </div>
                </div>
            ) : null}

            {stepKey === 'entry' ? (
                <div className="tw-orderwiz-block">
                    <div className="tw-orderwiz-block__title">Entrada (opcional)</div>

                    <div className="tw-orderwiz-omitrow">
                        <button
                            className={`tw-orderwiz-omit ${pickedEntryId == null ? 'tw-orderwiz-omit--active' : ''}`}
                            type="button"
                            onClick={() => {
                                touchedRef.current.entry = true;
                                setPickedEntryId(null);
                            }}
                        >
                            Sin entrada
                        </button>
                    </div>

                    <div className="tw-orderwiz-grid">
                        {entryChoices.map((dish) => (
                            <DishCard
                                key={dish.id}
                                dish={dish}
                                selected={String(pickedEntryId) === String(dish.id)}
                                onSelect={() => {
                                    touchedRef.current.entry = true;
                                    setPickedEntryId(dish.id);
                                }}
                            />
                        ))}
                    </div>
                </div>
            ) : null}

            {stepKey === 'snack' && slotType !== 'cena' ? (
                <div className="tw-orderwiz-block">
                    <div className="tw-orderwiz-block__title">Snack (opcional)</div>

                    <div className="tw-orderwiz-omitrow">
                        <button
                            className={`tw-orderwiz-omit ${pickedSnackId == null ? 'tw-orderwiz-omit--active' : ''}`}
                            type="button"
                            onClick={() => {
                                touchedRef.current.snack = true;
                                setPickedSnackId(null);
                            }}
                        >
                            Sin snack
                        </button>
                    </div>

                    <div className="tw-orderwiz-grid">
                        {snackChoices.map((dish) => (
                            <DishCard
                                key={dish.id}
                                dish={dish}
                                selected={String(pickedSnackId) === String(dish.id)}
                                onSelect={() => {
                                    touchedRef.current.snack = true;
                                    setPickedSnackId(dish.id);
                                }}
                            />
                        ))}
                    </div>
                </div>
            ) : null}

            {stepKey === 'sum' ? (
                <div className="tw-orderwiz-block">
                    <div className="tw-orderwiz-block__title">Resumen</div>

                    <div className="tw-orderwiz-summary">
                        <div className="tw-orderwiz-summary__row">
                            <div className="tw-orderwiz-summary__label">Plato fuerte</div>
                            <div className="tw-orderwiz-summary__val">{mainDish?.nombre || '—'}</div>
                        </div>

                        <div className="tw-orderwiz-summary__row">
                            <div className="tw-orderwiz-summary__label">Entrada</div>
                            <div className="tw-orderwiz-summary__val">{pickedEntryId ? dishMap?.[String(pickedEntryId)]?.nombre : 'Sin entrada'}</div>
                        </div>

                        {slotType !== 'cena' ? (
                            <div className="tw-orderwiz-summary__row">
                                <div className="tw-orderwiz-summary__label">Snack</div>
                                <div className="tw-orderwiz-summary__val">{pickedSnackId ? dishMap?.[String(pickedSnackId)]?.nombre : 'Sin snack'}</div>
                            </div>
                        ) : null}

                        <div className="tw-orderwiz-summary__hint">{mode === 'create' ? 'Confirmar consume 1 envío.' : 'Guardar cambios no consume envíos.'}</div>
                    </div>
                </div>
            ) : null}

            <div className="tw-orderwiz-foot">
                <button className="tw-orderwiz-btn tw-orderwiz-btn--ghost" onClick={goPrev} type="button" disabled={step === 0}>
                    Atrás
                </button>

                {step < maxStep ? (
                    <button
                        className={`tw-orderwiz-btn tw-orderwiz-btn--primary ${pickedOptionId ? '' : 'tw-orderwiz-btn--disabled'}`}
                        onClick={pickedOptionId ? goNext : undefined}
                        type="button"
                        disabled={!pickedOptionId}
                    >
                        Siguiente
                    </button>
                ) : (
                    <button
                        className={`tw-orderwiz-btn tw-orderwiz-btn--primary ${mode === 'create' ? (canConfirmCreate ? '' : 'tw-orderwiz-btn--disabled') : canConfirmUpdate ? '' : 'tw-orderwiz-btn--disabled'
                            }`}
                        onClick={(mode === 'create' ? canConfirmCreate : canConfirmUpdate) ? handleConfirm : undefined}
                        type="button"
                        disabled={!(mode === 'create' ? canConfirmCreate : canConfirmUpdate)}
                    >
                        <CheckIcon /> {mode === 'create' ? 'Confirmar pedido' : 'Guardar cambios'}
                    </button>
                )}
            </div>
        </div>
    );
}
